/**
 * Impersonation claim helpers with no module imports.
 *
 * Both services/api.ts and services/auth.ts need them, and those two modules cannot
 * import each other (see the circular-import note in api.ts), so the JWT decoding
 * lives here and auth.ts re-exports it.
 */

const TOKEN_STORAGE_KEY = 'auth_token';

export interface JwtPayload {
  exp?: number;
  iat?: number;
  impersonator_id?: number;
  data?: { user?: { id: string } };
}

/**
 * Decode a JWT payload without verification (for local checks such as expiration).
 * JWT format: header.payload.signature (all base64-encoded).
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode base64url payload (handle URL-safe chars and padding)
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    while (payload.length % 4) {
      payload += '=';
    }
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is an impersonation token (has impersonator_id claim)
 */
export function getImpersonatorId(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return payload?.impersonator_id ?? null;
}

// @MX:ANCHOR: [AUTO] Single client-side source for "is this session impersonating" at write time.
// @MX:REASON: Every stored-settings write (user preferences, export selections, custom presets,
// question options) is dropped on this answer. It reads the impersonator claim of the token in
// localStorage rather than the per-tab sessionStorage flag AuthContext keeps for the admin's
// own token: the token swap is the identity switch, so a tab opened without that flag, the
// moment between setting the flag and swapping the token, and the moment between clearing the
// flag and restoring the token all gate correctly. The server refuses the same writes with 403
// (services/preferences_write_safety.py).
export function isImpersonatingSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  return token != null && getImpersonatorId(token) != null;
}
